import {FC, useEffect} from 'react'
import {dispatch} from 'app/store'
import {policiesActions} from 'features/Policies/policiesSlice'
import {Policy, PolicyRoutesEnum, SliceStatus} from 'interfaces'
import {LoadingSpinner, RenderIf} from 'components/Basic'
import classNames from 'classnames'
import {useActions, usePolicy} from 'hooks'
import {prepareCloudAppSave} from 'features/Policies/CloudApp/cloudApp.utils'
import {ActionContainer} from 'components/Layouts'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {
  selectIsCloudAppSaving,
  selectPolicies,
} from 'features/Policies/policiesSelectors'
import {colorCodes, trailingSlashRegEx} from 'definitions/constant'
import {Button} from 'design'
import {CanIAccess, PERMISSIONS, withPermission} from 'rbac'

const contentFooterPermissions = [
  PERMISSIONS.ADD_EXCEPTION.WRITE,
  PERMISSIONS.URL_CUSTOM_CATEGORY.WRITE,
  PERMISSIONS.ADD_EXCEPTIONS_TO_CUSTOM_CATEGORIES.WRITE,
]

export const PoliciesFooter: FC = () => {
  const {
    updatePolicyStatus,
    policyAssignee,
    cloudApp,
    updatedPolicyField,
    formSubmitted,
    savePressed,
  } = useSelector(selectPolicies)
  const {isBasePolicyToBeColored, pathname, selectedPolicyName} = usePolicy()
  const {isSnackbarActionAvailable} = useActions()
  const canAssignPolicyUsers = withPermission([
    PERMISSIONS.ASSIGN_USERS_TO_POLICY.WRITE,
  ])
  const {t} = useTranslation(['policies', 'common'])

  const normalizedPathname = pathname.replace(trailingSlashRegEx, '')
  const isUsersPath = normalizedPathname === PolicyRoutesEnum.USERS
  const isCloudAppPath = normalizedPathname === PolicyRoutesEnum.CLOUD_APP
  const isCloudAppSaving = useSelector(selectIsCloudAppSaving)
  const pendingState =
    updatePolicyStatus === SliceStatus.pending ||
    (isCloudAppPath && cloudApp.loadStatus === SliceStatus.pending) ||
    isCloudAppSaving
  const isContentPath = normalizedPathname === PolicyRoutesEnum.POLICIES_CONTENT
  const footerPermissions = isUsersPath
    ? [PERMISSIONS.ASSIGN_USERS_TO_POLICY.WRITE]
    : contentFooterPermissions

  const onCancel = () => {
    // remove trailing slash, if any
    switch (normalizedPathname) {
      case PolicyRoutesEnum.USERS:
        dispatch(policiesActions.removeAssignedPolicySnapshot())
        break
      case PolicyRoutesEnum.CLOUD_APP:
        dispatch(policiesActions.removeCloudAppSnapshot())
        break
    }
  }

  const saveChanges = () => {
    dispatch(policiesActions.formSubmitted(false))
    dispatch(policiesActions.savePressed(false))

    if (!isCloudAppPath) return

    const {requestCac, saved} = prepareCloudAppSave(
      cloudApp.data,
      cloudApp.baseCloudApp,
      selectedPolicyName !== Policy.BasePolicy,
    )

    dispatch(
      policiesActions.updateCloudApp({
        request: {
          policyName: selectedPolicyName,
          cac: requestCac,
        },
        saved,
      }),
    )
  }

  const savePolicyAssignments = () => {
    const payload = {
      policyName: selectedPolicyName,
      usersGroups: policyAssignee.data,
    }

    dispatch(policiesActions.assignPolicy(payload))
  }

  const submitCloudAppChanges = () => {
    if (updatedPolicyField.value1) {
      document.getElementById('cloud-app-submit')?.click()
      return
    }

    saveChanges()
  }

  const submitContentChanges = () => {
    const shouldSubmitContentForms =
      updatedPolicyField.value1 || updatedPolicyField.value2

    if (!shouldSubmitContentForms) {
      saveChanges()
      return
    }

    if (updatedPolicyField.value1) {
      document.getElementById('custom-category-submit')?.click()
    }

    if (updatedPolicyField.value2) {
      document.getElementById('submit-executable')?.click()
    }
  }

  const onSaveChanges = () => {
    if (isUsersPath && !canAssignPolicyUsers) return

    dispatch(policiesActions.savePressed(true))
    dispatch(policiesActions.formSubmitted(false))

    if (isUsersPath) {
      savePolicyAssignments()
      return
    }

    if (isCloudAppPath) {
      submitCloudAppChanges()
      return
    }

    if (isContentPath) {
      submitContentChanges()
    }
  }

  useEffect(() => {
    if (formSubmitted && savePressed) saveChanges()
  }, [formSubmitted, savePressed])

  return (
    <CanIAccess reqPermissions={footerPermissions}>
      <ActionContainer
        className={classNames(
          'border-t-0.5 border-gray-400 flex justify-end items-center',
          {
            'bg-dope-teal-100': isBasePolicyToBeColored,
            'bg-dope-black': !isBasePolicyToBeColored,
            hidden: !isSnackbarActionAvailable,
          },
        )}
      >
        <p
          className="text-white karla-bold text-sm mr-6"
          dangerouslySetInnerHTML={{
            __html: t('common:SAVE_FOOTER_MESSAGE'),
          }}
        ></p>
        <Button
          className="mr-3"
          size="md"
          borderRadius="rounded"
          colorScheme={isBasePolicyToBeColored ? 'cyan' : 'red'}
          onClick={onCancel}
          disabled={pendingState}
          data-cy="policy-footer-cancel"
        >
          {t('common:CANCEL')}
        </Button>

        <Button
          size="sm"
          className="mr-6"
          borderRadius="rounded"
          onClick={onSaveChanges}
          disabled={pendingState}
          data-cy="policy-save"
        >
          <RenderIf isTrue={!pendingState}>{t('common:SAVE')}</RenderIf>
          <RenderIf isTrue={pendingState}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.dopeBlack}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </ActionContainer>
    </CanIAccess>
  )
}
